import { Trans, useTranslation } from 'react-i18next'

import { IOrganizationMemberInviteMetadata } from '@makyoapp/makyo-sdk/models/NotificationsMetaData'
import { cleanEmail } from '@makyoapp/makyo-common'
import { AvatarSize, FallbackAvatar, ShapeVariant } from '@makyoapp/makyo-ui'

import { InviteMetadata } from '@/hooks/useInvite'

/**
 * InviteHeader is a function component that displays an invitation header,
 * including organization name, invitee avatar, name, and email, as well as
 * instructions based on the current step.
 *
 * @param {Object} props - The component's props.
 * @param {number} props.step - The current step of the invitation process.
 * @param {InviteMetadata} [props.data] - The metadata of the invite, including
 * organization and invitee details.
 *
 * @returns {JSX.Element} A div element containing the invitation header.
 */
function InviteHeader({ step, data }: { step: number; data?: InviteMetadata }) {
  const { t } = useTranslation()
  const orgData = data as IOrganizationMemberInviteMetadata
  const orgName = orgData.OrgName
  const inviteeName = orgData.InviteeName
  const inviteeEmail = orgData.InviteeEmail
  const inviteeAvatar = orgData.InviteeAvatar

  const finalText = cleanEmail(inviteeEmail)
  const finalName = inviteeName || finalText

  return (
    <div className='w-full'>
      <h2 className='text-center text-lg font-bold font-poppins'>
        <Trans>
          Join <span className='link'>{orgName}</span> on Makyo
        </Trans>
      </h2>
      <div className='my-2 w-full flex-cc flex-col'>
        <FallbackAvatar
          text={finalText}
          src={inviteeAvatar}
          email={inviteeEmail}
          size={AvatarSize.L}
          shape={ShapeVariant.Rounded}
        />
        <div>
          <Trans>
            <strong>{finalName}</strong> already joined
          </Trans>
        </div>
      </div>
      <h2 className='text-center text-lg font-bold font-poppins'>
        {step === 2 && <div>{t('Check your email for a code')}</div>}
        {step === 3 && <div>{t('Enter 2FA Code')}</div>}
      </h2>
    </div>
  )
}

export default InviteHeader
